import { ListBox, Popover } from "@jugl-web/ui-components";
import { cx, useTranslations, dateToISOWithoutTimezone } from "@jugl-web/utils";
import { useDueInOptions } from "@web-src/modules/invoices/hooks/useDueInOptions";
import { useInvoicesItemFormPageContext } from "@web-src/modules/invoices/pages/InvoicesItemFormPage/context/useInvoicesItemFormPageContext";
import { useEffect, useMemo, useRef, useState } from "react";
import format from "date-fns/format";
import { DatePicker } from "@jugl-web/ui-components/cross-platform/DatePicker";
import differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import addDays from "date-fns/addDays";
import startOfDay from "date-fns/startOfDay";
import { usePrevious } from "react-use";
import { ReactComponent as CalendarIcon } from "./assets/calendar.svg";
import { ReactComponent as ChevronDownIcon } from "./assets/chevron-down.svg";

export const DueDateField = () => {
  const { t } = useTranslations();
  const { formValues, setFieldValue } = useInvoicesItemFormPageContext();
  const { options } = useDueInOptions(true);
  const [isCustomDatePickerOpen, setIsCustomDatePickerOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const issueDate = useMemo(
    () =>
      formValues.date ? new Date(formValues.date) : startOfDay(new Date()),
    [formValues.date]
  );

  const selectedOption = useMemo(() => {
    const days = differenceInCalendarDays(
      formValues.due_on ? new Date(formValues.due_on) : new Date(),
      issueDate
    );

    const matchingOption = options.find(
      (option) => option.id === days?.toString()
    );
    return matchingOption || options.find((option) => option.id === "custom");
  }, [formValues.due_on, issueDate, options]);

  const displayStr = useMemo(() => {
    if (selectedOption?.id === "custom" && formValues.due_on) {
      return format(new Date(formValues.due_on), "dd MMM yyyy");
    }
    return selectedOption?.value || "-";
  }, [selectedOption?.id, selectedOption?.value, formValues.due_on]);

  const previousIssueDate = usePrevious(issueDate);
  const previousSelectedOption = usePrevious(selectedOption);
  useEffect(() => {
    if (!previousSelectedOption) return;
    if (previousIssueDate && issueDate !== previousIssueDate) {
      if (previousSelectedOption?.id !== "custom") {
        setFieldValue(
          "due_on",
          dateToISOWithoutTimezone(
            addDays(issueDate, parseInt(previousSelectedOption.id, 10))
          )
        );
      }
    }
  }, [issueDate, previousIssueDate, previousSelectedOption, setFieldValue]);

  return (
    <div>
      <label className="mb-2 block pl-2.5 font-secondary text-[15px] font-normal text-dark-700">
        {t({
          id: "invoices.item.dueIn",
          defaultMessage: "Due in",
        })}
      </label>
      <div className="w-full" ref={wrapperRef}>
        <Popover
          placement="bottom-start"
          className="px-3 py-2"
          renderTrigger={({ Trigger, triggerRef, isOpen }) => (
            <Trigger
              ref={triggerRef}
              className="flex h-14 w-full cursor-pointer items-center justify-between rounded-xl border border-solid border-grey-400 bg-transparent px-5 text-base font-normal text-dark outline-none"
            >
              {displayStr}
              <ChevronDownIcon
                className={cx(
                  "shrink-0 transition-transform",
                  isOpen && "rotate-180"
                )}
              />
            </Trigger>
          )}
        >
          {({ onClose }) => (
            <div
              className={cx(
                "relative",
                isCustomDatePickerOpen && "max-w-[340px]"
              )}
              style={{ width: (wrapperRef.current?.offsetWidth || 0) - 24 }}
            >
              {isCustomDatePickerOpen ? (
                <div className="mt-2 w-full">
                  <DatePicker
                    initialDate={
                      formValues.due_on
                        ? new Date(formValues.due_on)
                        : undefined
                    }
                    minDate={
                      formValues.date ? new Date(formValues.date) : undefined
                    }
                    onSubmit={(date) => {
                      onClose();
                      setFieldValue(
                        "due_on",
                        date ? dateToISOWithoutTimezone(date) : ""
                      );
                    }}
                    showCancelButton
                    onClose={() => setIsCustomDatePickerOpen(false)}
                  />
                </div>
              ) : (
                <ListBox
                  items={options}
                  selectionBehavior={{ mode: "single", canToggle: true }}
                  onSelect={(item) => {
                    if (item.id !== "custom") {
                      setFieldValue(
                        "due_on",
                        dateToISOWithoutTimezone(
                          addDays(issueDate, parseInt(item.id, 10))
                        )
                      );
                      onClose();
                    } else {
                      setTimeout(() => {
                        setIsCustomDatePickerOpen(true);
                      }, 300);
                    }
                  }}
                  defaultSelectedIds={selectedOption ? [selectedOption.id] : []}
                  renderLabel={(item) => item.value}
                  renderEndIcon={(item) =>
                    item.id === "custom" ? <CalendarIcon /> : null
                  }
                  itemSize="sm"
                  spaceBetweenItems="compact"
                />
              )}
            </div>
          )}
        </Popover>
      </div>
    </div>
  );
};
